/** Разрешённые inline-теги в ответах толкования (остальное экранируется). */
const ALLOWED_INLINE_TAG_RE = /<\/?(?:b|strong|em|i|br)\s*\/?>/gi;

const ANCHOR_TAG_RE =
  /<a\s+href=["']([^"']*)["'][^>]*>([\s\S]*?)<\/a>/gi;

const TG_STARTAPP_RE =
  /https:\/\/t\.me\/VerbumBibleBot\/app\?startapp=([A-Za-z0-9_]+)/gi;

function escapeHtmlText(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function escapeHtmlAttr(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;');
}

function normalizeAllowedTag(match) {
  const m = String(match).trim().toLowerCase();
  if (m === '<br>' || m === '<br/>' || m === '<br />') return '<br/>';
  if (m === '<b>') return '<b>';
  if (m === '</b>') return '</b>';
  if (m === '<strong>') return '<strong>';
  if (m === '</strong>') return '</strong>';
  if (m === '<em>') return '<em>';
  if (m === '</em>') return '</em>';
  if (m === '<i>') return '<i>';
  if (m === '</i>') return '</i>';
  return match;
}

/** Путь в приложении из startapp= (как в ChatStreamView). */
export function startParamToBiblePath(startParam, translation = 'synodal') {
  const raw = String(startParam || '').trim();
  if (!raw) return `/${translation}`;
  const parts = raw.split('_').filter(Boolean);
  if (parts.length === 0) return `/${translation}`;

  const isOldFormat = /^\d+$/.test(parts[0]);
  let tr = translation;
  let bookId;
  let chapter;
  let verse;
  let action = null;

  if (isOldFormat) {
    bookId = parts[0];
    chapter = parts[1];
    verse = parts[2];
    const last = parts[parts.length - 1];
    if (last === 'parallel' || last === 'interpretation' || last === 'image') action = last;
  } else {
    tr = parts[0] || translation;
    bookId = parts[1];
    chapter = parts[2];
    verse = parts[3];
    const last = parts[parts.length - 1];
    if (last === 'parallel' || last === 'interpretation' || last === 'image') action = last;
  }

  let path = `/${tr}`;
  if (bookId) path += `/${bookId}`;
  if (chapter) path += `/${chapter}`;
  if (verse) path += `/${verse}`;
  if (action && verse) path += `/${action}`;
  return path;
}

function sanitizeInsightHref(href, translation) {
  const h = String(href || '').trim();
  if (!h) return null;

  const tg = h.match(/https:\/\/t\.me\/VerbumBibleBot\/app\?startapp=([A-Za-z0-9_]+)/i);
  if (tg) return startParamToBiblePath(tg[1], translation);

  if (/^\/[a-z0-9_-]+\/\d+\/\d+(?:\/\d+)?(?:\/(?:parallel|interpretation|image))?$/i.test(h)) {
    return h;
  }

  if (/^https:\/\/t\.me\/VerbumBibleBot\//i.test(h)) return h;

  return null;
}

function formatInlineSegment(segment) {
  const placeholders = [];
  const withSlots = String(segment || '').replace(ALLOWED_INLINE_TAG_RE, (match) => {
    const slot = `\uE000${placeholders.length}\uE001`;
    placeholders.push(normalizeAllowedTag(match));
    return slot;
  });

  let html = escapeHtmlText(withSlots);

  placeholders.forEach((tag, i) => {
    html = html.split(`\uE000${i}\uE001`).join(tag);
  });

  return html
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(
      /<\/(strong|b|em|i)>(?=[A-Za-z\u0400-\u04FF0-9])/gi,
      (_, tag) => `</${tag}> `
    );
}

function extractAnchors(raw, translation) {
  const anchors = [];
  const withoutAnchors = raw.replace(ANCHOR_TAG_RE, (full, href, label) => {
    const safeHref = sanitizeInsightHref(href, translation);
    if (!safeHref) {
      return formatInlineSegment(full);
    }
    const slot = `\uE010${anchors.length}\uE011`;
    anchors.push(
      `<a href="${escapeHtmlAttr(safeHref)}" class="bf-insight-ref">${formatInlineSegment(label)}</a>`
    );
    return slot;
  });

  return { withoutAnchors, anchors };
}

/**
 * Текст ответа ассистента → безопасный HTML для dangerouslySetInnerHTML.
 * Поддерживает <b>, <strong>, <em>, <i>, <br/>, <a href="…"> (t.me / внутренние пути).
 */
export function formatAssistantInsightHtml(text, { translation = 'synodal' } = {}) {
  let raw = String(text || '').trim();
  if (!raw) return '';

  raw = raw.replace(TG_STARTAPP_RE, (_, startParam) => {
    const path = startParamToBiblePath(startParam, translation);
    return escapeHtmlAttr(path);
  });

  const { withoutAnchors, anchors } = extractAnchors(raw, translation);
  let html = formatInlineSegment(withoutAnchors);

  anchors.forEach((anchorHtml, i) => {
    html = html.split(`\uE010${i}\uE011`).join(anchorHtml);
  });

  html = html
    .replace(/\\n/g, '\n')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n');

  html = html.replace(/\n\n+/g, '</p><p>').replace(/\n/g, '<br/>');

  return html;
}

/** Обёртка для prose-блока (не ломает несколько <p> и ссылки). */
export function wrapInsightProseHtml(html) {
  const body = String(html || '').trim();
  if (!body) return '';
  if (/<p[\s>]/i.test(body)) return body;
  return `<div class="bf-insight-prose">${body}</div>`;
}
